import { getToken } from '@/shared/utils/get-token';

const COGNITO_USERNAME_CLAIM = 'cognito:username';

const decodeBase64Url = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (base64.length % 4)) % 4;
  return atob(base64 + '='.repeat(padding));
};

export const decodeTokenClaims = (
  token: string
): { uuid: string; exp: number | null } => {
  const [, payload] = token.split('.');
  const claims = JSON.parse(decodeBase64Url(payload)) as Record<
    string,
    unknown
  >;
  const uuid = claims[COGNITO_USERNAME_CLAIM];
  // Coercing instead of checking used to turn a missing claim into the string "undefined",
  // which is truthy, so the socket subscribed to presence-customer.<id>.undefined and the
  // customer waited on a channel nobody publishes to. Silence, not an error.
  if (typeof uuid !== 'string' || uuid === '') {
    throw new Error('Bingo pass has no cognito:username claim');
  }
  const exp = Number(claims.exp);
  return { uuid, exp: Number.isFinite(exp) ? exp : null };
};

// A pass that is not a well-formed JWT reaches decodeTokenClaims as an undefined payload
// segment and throws. Callers that are mid-reconnect cannot let that escape, because the
// old socket is already gone by then and the throw would strand the player with no error
// screen.
export const tryDecodeTokenClaims = (
  token: string
): { uuid: string; exp: number | null } | null => {
  try {
    return decodeTokenClaims(token);
  } catch {
    return null;
  }
};

export const BINGO_TOKEN_REFRESH_TIMEOUT_MS = 10000;

// getToken swallows its own request errors and resolves undefined rather than
// rejecting, but nothing bounds how long it waits first: the cognito proxy has been
// seen hanging instead of erroring under load, well past what the caller's own retry
// loop assumes a "no pass" answer took. Racing it against a budget turns "still
// waiting" into a definite failure the caller can show an error screen for, rather
// than leaving the customer on a frozen page for however long the proxy takes to give
// up.
export const refreshBingoToken = (): Promise<string | undefined> =>
  Promise.race([
    getToken(true),
    new Promise<undefined>((resolve) => {
      window.setTimeout(
        () => resolve(undefined),
        BINGO_TOKEN_REFRESH_TIMEOUT_MS
      );
    })
  ]);
